import React, { useState, useEffect } from "react"
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts"

import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card"
import Button from "../../../components/ui/Button"
import { ChartContainer, ChartTooltip } from "../../../components/ui/line-charts-2"
import { getMonthlyRevenue } from "../../../utils/dashboardAnalyticsHelpers"

const chartConfig = { revenue: { label: "Revenue", color: "#f97316" } }

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value || 0)

const RevenueTrendCard = ({ orgId, onNavigateTab }) => {
  const [months, setMonths] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    getMonthlyRevenue(orgId).then((data) => {
      setMonths(data)
      setLoading(false)
    })
  }, [orgId])

  const total = months?.reduce((sum, m) => sum + (m?.revenue || 0), 0)
  const currentMonth = months?.[months.length - 1]?.revenue || 0
  const prevMonth = months?.[months.length - 2]?.revenue || 0
  const momChange = prevMonth > 0 ? Math.round(((currentMonth - prevMonth) / prevMonth) * 1000) / 10 : null

  return (
    <Card className="flex w-full flex-col gap-0 p-5 shadow-none">
      <CardHeader className="flex flex-row items-center justify-between p-0">
        <CardTitle className="text-base font-medium leading-none font-semibold text-muted-foreground">
          Revenue Trend
        </CardTitle>
        <Button variant="outline" size="sm" className="h-7.5 px-2.5 text-muted-foreground" onClick={() => onNavigateTab?.("commerce-revenue")}>
          Report
        </Button>
      </CardHeader>

      <CardContent className="flex flex-col gap-4 p-0">
        <div className="flex items-center gap-2 pb-2">
          <span className="text-3xl font-medium tracking-tight tabular-nums">{formatCurrency(total)}</span>
          {momChange !== null && (
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                momChange >= 0 ? "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-600" : "bg-red-100 text-destructive dark:bg-red-950"
              }`}
            >
              {momChange >= 0 ? "+" : ""}
              {momChange}%
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground -mt-2">Last 12 months</p>

        {loading ? (
          <div className="h-[180px] w-full rounded-lg bg-muted animate-pulse" />
        ) : (
          <ChartContainer config={chartConfig} className="aspect-auto h-[180px] w-full">
            <LineChart accessibilityLayer data={months}>
              <CartesianGrid vertical={false} strokeDasharray="4 4" stroke="var(--color-border)" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
              <YAxis hide domain={["dataMin - 10", "dataMax + 10"]} />
              <ChartTooltip
                formatter={(value) => formatCurrency(value)}
                labelFormatter={(label) => label}
                cursor={{ stroke: "#f97316", strokeWidth: 1 }}
                contentStyle={{ background: "var(--color-card)", border: "1px solid var(--color-border)", borderRadius: "8px", fontSize: "12px" }}
              />
              <Line type="monotone" dataKey="revenue" stroke="#f97316" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: "#f97316", stroke: "#ffffff", strokeWidth: 2 }} />
            </LineChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  )
}

export default RevenueTrendCard